import { useState, useEffect } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Users, RefreshCw, Tag, ChevronRight, Calendar } from "lucide-react";

export default function StylistClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/my/portal").then(res => {
      setClients(res.data?.clients || []);
    }).catch(() => toast.error("Failed to load clients")).finally(() => setLoading(false));
  }, []);

  const filtered = clients.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.services.some(s => s.toLowerCase().includes(search.toLowerCase()))
  );

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
    catch { return ""; }
  };

  if (loading) {
    return <div className="p-8 flex items-center justify-center min-h-[400px]"><RefreshCw className="w-5 h-5 animate-spin text-rose-400" /></div>;
  }

  return (
    <div data-testid="stylist-clients-page" className="p-6 lg:p-8 max-w-[1100px] mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900 tracking-tight">My Clients</h1>
          <p className="text-sm text-slate-400 mt-0.5">{clients.length} clients you've served</p>
        </div>
      </div>

      <input
        type="text"
        placeholder="Search by name or service..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full max-w-sm px-4 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-[#B5A493]"
        data-testid="client-search"
      />

      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
          <Users className="w-8 h-8 mx-auto mb-2 text-[#D1D1D1]" />
          <p className="text-slate-400">No clients found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((c, i) => (
            <div key={c.client_id} data-testid={`client-card-${i}`} className="bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-sm transition-shadow">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-['Outfit'] font-semibold text-slate-900">{c.name}</p>
                  <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-400">
                    <Calendar className="w-3 h-3" />
                    <span>{c.visit_count} visits</span>
                    {c.last_visit && <span>&middot; Last: {fmtDate(c.last_visit)}</span>}
                  </div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                  c.pipeline_stage === "vip" ? "bg-[#F5E6D3] text-[#92400E]" :
                  c.pipeline_stage === "active" ? "bg-[#D4EDDA] text-[#155724]" :
                  c.pipeline_stage === "at-risk" ? "bg-[#FDE8E8] text-rose-600" :
                  "bg-slate-50 text-slate-400"
                }`}>{c.pipeline_stage}</span>
              </div>

              {c.services.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {c.services.map((s, si) => (
                    <span key={`svc-${s}-${si}`} className="text-[10px] bg-[#FAFAFA] text-slate-600 px-2 py-0.5 rounded border border-slate-200">{s}</span>
                  ))}
                </div>
              )}

              {c.tags.length > 0 && (
                <div className="flex items-center gap-1 flex-wrap">
                  <Tag className="w-3 h-3 text-rose-400" />
                  {c.tags.map((t, ti) => (
                    <span key={`tag-${t}-${ti}`} className="text-[10px] text-rose-400 bg-[#FAFAF9] px-1.5 py-0.5 rounded">{t}</span>
                  ))}
                </div>
              )}

              {c.notes && (
                <p className="text-xs text-slate-400 mt-2 bg-[#FAFAF9] rounded p-2 border border-slate-100">{c.notes}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
